import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  CircularProgress,
  FormHelperText,
  Typography,
} from "@mui/material";
import AccountSearch from "../../../../components/common/AccountSearch";
import { CheckTypeAvailability } from "../../../services/ClientWaterProfileService";

const DuplicateWaterProfileDialog = ({ open, onClose, onConfirm }) => {
  const [formData, setFormData] = useState({
    accountNumber: "",
    type: "",
  });

  const [errors, setErrors] = useState({});
  const [checkingType, setCheckingType] = useState(false);
  const [typeValidationStatus, setTypeValidationStatus] = useState(null);
  const [accountValid, setAccountValid] = useState(false);

  useEffect(() => {
    if (open) {
      setFormData({ accountNumber: "", type: "" });
      setErrors({});
      setCheckingType(false);
      setTypeValidationStatus(null);
      setAccountValid(false);
    }
  }, [open]);

  useEffect(() => {
    let timeoutId;
    const checkType = async () => {
      if (accountValid && formData.accountNumber && formData.type.trim()) {
        setCheckingType(true);
        setTypeValidationStatus(null);

        timeoutId = setTimeout(async () => {
          try {
            const response = await CheckTypeAvailability({
              accountNumber: String(formData.accountNumber),
              type: String(formData.type),
            });

            if (
              response &&
              response.statusCode === 200 &&
              response.results === true
            ) {
              setTypeValidationStatus("available");
              setErrors((prev) => ({ ...prev, type: "" }));
            } else {
              setTypeValidationStatus("unavailable");
              setErrors((prev) => ({
                ...prev,
                type:
                  "This type/water profile name is not available for this client.",
              }));
            }
          } catch (error) {
            setTypeValidationStatus("unavailable");
            setErrors((prev) => ({
              ...prev,
              type: "Error checking type availability.",
            }));
          } finally {
            setCheckingType(false);
          }
        }, 500);
      } else {
        setTypeValidationStatus(null);
        setCheckingType(false);
      }
    };

    checkType();

    return () => clearTimeout(timeoutId);
  }, [formData.type, formData.accountNumber, accountValid]);

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (field === "type" && value.trim()) {
      setTypeValidationStatus(null);
    }
  };

  const handleSubmit = () => {
    const newErrors = {};
    if (!formData.accountNumber)
      newErrors.accountNumber = "Account number is required";
    if (!formData.type)
      newErrors.type = "Type (profile name) is required";

    if (typeValidationStatus === "unavailable") {
      newErrors.type =
        "This type/water profile name is not available for this client.";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      onConfirm(formData); // Pass data to parent
    }
  };

  const getTypeHelperText = () => {
    if (checkingType) return "Checking availability...";
    if (typeValidationStatus === "available")
      return "Type name is available";
    return errors.type || "";
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Duplicate Water Profile</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="body2">
              Enter account number and water profile name to check availability.
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <AccountSearch
              value={formData.accountNumber}
              onChange={handleChange("accountNumber")}
              isValidated={setAccountValid}
            />
            {errors.accountNumber && (
              <FormHelperText error>{errors.accountNumber}</FormHelperText>
            )}
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Water Profile Type"
              value={formData.type}
              onChange={handleChange("type")}
              size="small"
              fullWidth
              required
              disabled={!accountValid}
              error={!!errors.type}
              helperText={getTypeHelperText()}
              InputProps={{
                endAdornment: checkingType ? (
                  <CircularProgress size={18} />
                ) : null,
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={checkingType || typeValidationStatus === "unavailable"}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DuplicateWaterProfileDialog;
